'use client';
import {
  CaretDoubleLeft,
  CaretDoubleRight,
  CaretLeft,
  CaretRight,
} from 'phosphor-icons';
import { useEffect, useMemo, useState } from 'react';
import { PaginationProps, PaginationState } from './Pagination.types';

const commonButtonClasses =
  'size-10 mx-1 rounded-lg cursor-pointer border text-grey-600 border-grey-300 text-base hover:bg-grey-100 flex items-center justify-center transition-colors';

const arrowButtonStyles = `${commonButtonClasses} disabled:bg-grey-300 disabled:cursor-not-allowed disabled:text-grey-400`;

const pageButtonStyles = `${commonButtonClasses} font-bold disabled:bg-blue disabled:border-blue disabled:text-white disabled:cursor-not-allowed`;

export const Pagination = ({
  currentPage: initialPage,
  totalPages,
  onPageChange,
  showFirstLast = false,
  maxVisiblePages = 5,
  className = '',
  microcopy,
}: PaginationProps) => {
  const [currentPage, setCurrentPage] = useState(initialPage);

  useEffect(() => {
    setCurrentPage(initialPage);
  }, [initialPage]);

  const {
    paginationAriaLabel,
    previousAriaLabel,
    nextAriaLabel,
    pageAriaLabel,
    firstAriaLabel,
    lastAriaLabel,
  } = microcopy;

  const paginationState: PaginationState = useMemo(() => {
    const hasPreviousPage = currentPage > 1;
    const hasNextPage = currentPage < totalPages;

    // Calculate visible pages
    const visiblePages: number[] = [];
    const halfVisible = Math.floor(maxVisiblePages / 2);

    let startPage = Math.max(1, currentPage - halfVisible);
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    // Adjust start if we're near the end
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      visiblePages.push(i);
    }

    return {
      currentPage,
      totalPages,
      hasPreviousPage,
      hasNextPage,
      visiblePages,
    };
  }, [currentPage, totalPages, maxVisiblePages]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    onPageChange(page);
  };

  const handlePreviousPage = () => {
    const newPage = Math.max(1, currentPage - 1);
    handlePageChange(newPage);
  };

  const handleNextPage = () => {
    const newPage = Math.min(totalPages, currentPage + 1);
    handlePageChange(newPage);
  };

  const handleFirstPage = () => {
    handlePageChange(1);
  };

  const handleLastPage = () => {
    handlePageChange(totalPages);
  };

  const isCurrentPage = (page: number) => page === currentPage;

  if (totalPages <= 1) {
    return null;
  }

  return (
    <nav
      data-test="pagination"
      aria-label={paginationAriaLabel}
      className={className}
    >
      <ul className="flex">
        {showFirstLast && paginationState.hasPreviousPage && (
          <li>
            <button
              aria-label={firstAriaLabel}
              className={arrowButtonStyles}
              onClick={handleFirstPage}
            >
              <CaretDoubleLeft size={20} role="presentation" />
            </button>
          </li>
        )}

        <li>
          <button
            aria-label={previousAriaLabel}
            className={arrowButtonStyles}
            disabled={!paginationState.hasPreviousPage}
            aria-disabled={!paginationState.hasPreviousPage}
            onClick={handlePreviousPage}
          >
            <CaretLeft size={20} role="presentation" />
          </button>
        </li>

        {paginationState.visiblePages.map((page) => (
          <li key={page}>
            <button
              aria-label={`${pageAriaLabel} ${page}`}
              className={pageButtonStyles}
              disabled={isCurrentPage(page)}
              aria-current={isCurrentPage(page) ? 'page' : undefined}
              onClick={() => handlePageChange(page)}
            >
              {page}
            </button>
          </li>
        ))}

        <li>
          <button
            aria-label={nextAriaLabel}
            className={arrowButtonStyles}
            disabled={!paginationState.hasNextPage}
            aria-disabled={!paginationState.hasNextPage}
            onClick={handleNextPage}
          >
            <CaretRight size={20} role="presentation" />
          </button>
        </li>

        {showFirstLast && paginationState.hasNextPage && (
          <li>
            <button
              aria-label={lastAriaLabel}
              className={arrowButtonStyles}
              onClick={handleLastPage}
            >
              <CaretDoubleRight size={20} role="presentation" />
            </button>
          </li>
        )}
      </ul>
    </nav>
  );
};
